import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { withRouter, RouteComponentProps } from "react-router-dom";
import classnames from "classnames";

import { BreadCrumb } from "../../../components/Common";
import {
  ActionButton,
  CourseDurationModal,
  TableSpinner,
} from "../../../components";
import { ActionTypeEnum } from "../../../enums";
import DeleteModal from "../../../components/Common/DeleteModal";
import ConfirmationModal from "../../../components/Common/ConfirmationModal";
import { AddEditEducationalStageModal } from "./AddEditEducationalStageModal";
import { AddGradeModal } from "./AddGradeModal";
import {
  EducationalStageDto,
  EducationalStageGradeDto,
} from "../../../interfaces/curriculum";
import { PaginateQuery } from "../../../interfaces";
import {
  useDeleteEducationalStageMutation,
  useGetEducationalStagesQuery,
  useRemoveGradeFromEducationalStageMutation,
} from "../../../store/api/educationalStageApi";
import { useGetCurriculumSelectListQuery } from "../../../store/api/curriculumApi";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";
import { useGetCourseSelectListQuery } from "../../../store/api/courseApi";
import { useRolloverMutation } from "../../../store/api/courseDurationApi";
import { toast } from "react-toastify";
import { buildSubjectsSearch } from "../../../utils/subjectsNavigation";

type TProps = RouteComponentProps & {};

const FILTER_KEYS = {
  educationSystemId: "educationSystemId",
  curriculumId: "curriculumId",
  courseId: "courseId",
} as const;

const readFiltersFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  return {
    educationSystemId: params.get(FILTER_KEYS.educationSystemId) || "",
    curriculumId: params.get(FILTER_KEYS.curriculumId) || "",
    courseId: params.get(FILTER_KEYS.courseId) || "",
  };
};

const EducationalStagesPageComponent: React.FC<TProps> = ({
  history,
  location,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const urlFilters = useMemo(
    () => readFiltersFromSearch(location.search),
    [location.search],
  );

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const selectedEducationSystemId = useMemo(() => {
    if (!educationSystems?.length) return urlFilters.educationSystemId;
    if (
      urlFilters.educationSystemId &&
      educationSystems.some((s: any) => s.id === urlFilters.educationSystemId)
    ) {
      return urlFilters.educationSystemId;
    }
    return educationSystems[0].id;
  }, [educationSystems, urlFilters.educationSystemId]);

  const { data: curricula } = useGetCurriculumSelectListQuery(
    selectedEducationSystemId || undefined,
    { skip: !selectedEducationSystemId },
  );
  const selectedCurriculumId = useMemo(() => {
    if (!curricula?.length) return "";
    if (
      urlFilters.curriculumId &&
      curricula.some((c: any) => c.id === urlFilters.curriculumId)
    ) {
      return urlFilters.curriculumId;
    }
    return curricula[0].id;
  }, [curricula, urlFilters.curriculumId]);

  const { data: courses } = useGetCourseSelectListQuery();
  const selectedCourseId = useMemo(() => {
    if (!courses?.length) return urlFilters.courseId;
    if (
      urlFilters.courseId &&
      courses.some((c: any) => c.id === urlFilters.courseId)
    ) {
      return urlFilters.courseId;
    }
    return courses[0].id;
  }, [courses, urlFilters.courseId]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteGradeModal, setShowDeleteGradeModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showAddGradeModal, setShowAddGradeModal] = useState(false);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [showRolloverModal, setShowRolloverModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [deletingGrade, setDeletingGrade] = useState<{
    stageId: string;
    gradeId: string;
  } | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [addingGradeStageId, setAddingGradeStageId] = useState<string | null>(
    null,
  );
  const [editingGrade, setEditingGrade] =
    useState<EducationalStageGradeDto | null>(null);
  const [durationTarget, setDurationTarget] = useState<{
    courseId: string;
    gradeId: string;
  } | null>(null);

  const syncFiltersToUrl = (
    next: {
      educationSystemId?: string;
      curriculumId?: string;
      courseId?: string;
    },
    mode: "push" | "replace" = "push",
  ) => {
    const params = new URLSearchParams();
    const educationSystemId =
      next.educationSystemId ?? selectedEducationSystemId;
    const curriculumId = next.curriculumId ?? selectedCurriculumId;
    const courseId = next.courseId ?? selectedCourseId;

    if (educationSystemId)
      params.set(FILTER_KEYS.educationSystemId, educationSystemId);
    if (curriculumId) params.set(FILTER_KEYS.curriculumId, curriculumId);
    if (courseId) params.set(FILTER_KEYS.courseId, courseId);

    const search = params.toString() ? `?${params.toString()}` : "";
    if (search === location.search) return;

    if (mode === "push") {
      history.push({ search });
    } else {
      history.replace({ search });
    }
  };

  // Fill missing/invalid URL params with resolved defaults (no history entry).
  useEffect(() => {
    if (
      !selectedEducationSystemId ||
      !selectedCurriculumId ||
      !selectedCourseId
    ) {
      return;
    }
    if (
      urlFilters.educationSystemId === selectedEducationSystemId &&
      urlFilters.curriculumId === selectedCurriculumId &&
      urlFilters.courseId === selectedCourseId
    ) {
      return;
    }
    syncFiltersToUrl(
      {
        educationSystemId: selectedEducationSystemId,
        curriculumId: selectedCurriculumId,
        courseId: selectedCourseId,
      },
      "replace",
    );
  }, [
    selectedEducationSystemId,
    selectedCurriculumId,
    selectedCourseId,
    urlFilters.educationSystemId,
    urlFilters.curriculumId,
    urlFilters.courseId,
  ]);

  const paginateQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId: selectedCurriculumId,
      courseId: selectedCourseId,
    };
    return q;
  }, [selectedCurriculumId, selectedCourseId]);

  const { data: response, isLoading } = useGetEducationalStagesQuery(
    paginateQuery,
    { skip: !selectedCurriculumId || !selectedCourseId },
  );
  const [deleteStage, { isLoading: deleting, error: deleteError }] =
    useDeleteEducationalStageMutation();
  const [
    removeGrade,
    { isLoading: deletingGradeLoading, error: deleteGradeError },
  ] = useRemoveGradeFromEducationalStageMutation();
  const [rollover, { isLoading: rolloverLoading, error: rolloverRawError }] =
    useRolloverMutation();

  const rolloverError = rolloverRawError
    ? (rolloverRawError as any)?.data?.message || "An error occurred"
    : undefined;

  document.title = `${t("documentTitle", { pageName: "educationalStages" })}`;

  const stages: EducationalStageDto[] = response?.data || [];

  const getCourseName = (id: string | null | undefined): string => {
    if (!id) return "";
    const course = courses?.find((c: any) => c.id === id)?.value;
    return course?.[langKey] || course?.en || "";
  };

  const getLatestDatedDurations = (grade: EducationalStageGradeDto) =>
    (grade.durations || [])
      .filter((d) => d.startAt || d.endAt)
      .slice()
      .sort((a, b) => {
        const aTime = new Date(
          (a.startAt || a.endAt) as string | Date,
        ).getTime();
        const bTime = new Date(
          (b.startAt || b.endAt) as string | Date,
        ).getTime();
        if (bTime !== aTime) return bTime - aTime;
        return (b.sequenceNumber || 0) - (a.sequenceNumber || 0);
      });

  const getDurationStatus = (
    grade: EducationalStageGradeDto,
  ): "danger" | "warning" | "success" => {
    const datedDurations = getLatestDatedDurations(grade);
    if (!datedDurations.length) return "danger";

    const latest = datedDurations[0];
    const now = Date.now();
    const start = latest.startAt ? new Date(latest.startAt).getTime() : null;
    const end = latest.endAt ? new Date(latest.endAt).getTime() : null;

    if (end !== null && end < now) return "danger";
    if (start !== null && start > now) return "warning";
    return "success";
  };

  const formatDurationSummary = (grade: EducationalStageGradeDto) => {
    const datedDurations = getLatestDatedDurations(grade);
    if (!datedDurations.length) return t("noDurations");

    const latest = datedDurations[0];
    const start = latest.startAt
      ? new Date(latest.startAt).toLocaleDateString()
      : "-";
    const end = latest.endAt
      ? new Date(latest.endAt).toLocaleDateString()
      : "-";
    const extra =
      datedDurations.length > 1 ? ` (+${datedDurations.length - 1})` : "";
    return `${start} - ${end}${extra}`;
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteStage(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {}
  };

  const onDeleteGradeConfirm = async () => {
    if (!deletingGrade) return;
    try {
      await removeGrade(deletingGrade).unwrap();
      setDeletingGrade(null);
      setShowDeleteGradeModal(false);
    } catch (err) {}
  };

  const onRolloverConfirm = async () => {
    if (!selectedCourseId || !selectedCurriculumId) return;
    try {
      await rollover({
        courseId: selectedCourseId,
        curriculumId: selectedCurriculumId,
      }).unwrap();
      setShowRolloverModal(false);
      toast.success(t("rolloverSuccess"), { autoClose: 3000 });
    } catch (err: any) {
      setShowRolloverModal(false);
      toast.error(err?.data?.message || t("rolloverFailed"), {
        autoClose: 3000,
      });
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("educationalStages")}
            pageTitle={t("educationSystem")}
          />
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3 small">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => history.push("/education-system/systems")}
            >
              {t("educationSystems")}
            </button>
            {selectedEducationSystemId ? (
              <>
                <span className="text-muted">/</span>
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() =>
                    history.push(
                      `/education-system/curricula?filter[educationSystemId]=${selectedEducationSystemId}`,
                    )
                  }
                >
                  {educationSystems?.find(
                    (s: any) => s.id === selectedEducationSystemId,
                  )?.value?.[langKey] ||
                    educationSystems?.find(
                      (s: any) => s.id === selectedEducationSystemId,
                    )?.value?.en ||
                    t("educationSystem")}
                </button>
              </>
            ) : null}
            {selectedCurriculumId ? (
              <>
                <span className="text-muted">/</span>
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() =>
                    history.push(
                      `/education-system/curricula?filter[educationSystemId]=${selectedEducationSystemId}`,
                    )
                  }
                >
                  {t("curricula")}
                </button>
                <span className="text-muted">/</span>
                <span className="fw-medium">
                  {curricula?.find((c: any) => c.id === selectedCurriculumId)
                    ?.value?.[langKey] ||
                    curricula?.find((c: any) => c.id === selectedCurriculumId)
                      ?.value?.en ||
                    t("curriculumItem")}
                </span>
              </>
            ) : null}
          </div>
          <Row className="mb-3 align-items-end g-3">
            <Col md={3}>
              <label className="form-label">{t("educationSystem")}</label>
              <Input
                type="select"
                value={selectedEducationSystemId}
                onChange={(e) => {
                  syncFiltersToUrl(
                    {
                      educationSystemId: e.target.value,
                      curriculumId: "",
                      courseId: selectedCourseId,
                    },
                    "push",
                  );
                }}
              >
                {(educationSystems || []).map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.value?.[langKey] || s.value?.en}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <label className="form-label">{t("curriculumItem")}</label>
              <Input
                type="select"
                value={selectedCurriculumId}
                onChange={(e) => {
                  syncFiltersToUrl(
                    {
                      educationSystemId: selectedEducationSystemId,
                      curriculumId: e.target.value,
                      courseId: selectedCourseId,
                    },
                    "push",
                  );
                }}
                disabled={!selectedEducationSystemId}
              >
                {(curricula || []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.value?.[langKey] || c.value?.en}
                  </option>
                ))}
              </Input>
            </Col>
            <Col className="col-sm-auto ms-auto">
              <div className="hstack gap-1">
                {selectedCourseId && selectedCurriculumId ? (
                  <Button
                    title={t("rolloverTitle", {
                      rollingToCourseName: getCourseName(selectedCourseId),
                    })}
                    color="danger"
                    onClick={() => setShowRolloverModal(true)}
                  >
                    <i className="ri-error-warning-line me-1 align-bottom"></i>
                    {t("rolloverTitle", {
                      rollingToCourseName: getCourseName(selectedCourseId),
                    })}
                  </Button>
                ) : null}
                <Button
                  color="primary"
                  onClick={() => {
                    setEditingItemId(null);
                    setShowAddEditModal(true);
                  }}
                  disabled={!selectedCurriculumId || !selectedCourseId}
                >
                  <i className="ri-add-fill me-1 align-bottom"></i>
                  {t("addStage")}
                </Button>
              </div>
            </Col>
          </Row>

          <Nav tabs className="nav-tabs-custom mb-3">
            {(courses || []).map((course: any) => (
              <NavItem key={course.id}>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: selectedCourseId === course.id,
                  })}
                  onClick={() => {
                    syncFiltersToUrl(
                      {
                        educationSystemId: selectedEducationSystemId,
                        curriculumId: selectedCurriculumId,
                        courseId: course.id,
                      },
                      "push",
                    );
                  }}
                >
                  {course.value?.[langKey] || course.value?.en}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <small className="text-muted d-block mb-2">
            {t("stageCourseGradeHint")}
          </small>
          <div className="d-flex flex-wrap gap-3 mb-3 small">
            <span className="text-danger">
              <i className="ri-checkbox-blank-circle-fill me-1 align-middle"></i>
              {t("durationStatusExpired")}
            </span>
            <span className="text-warning">
              <i className="ri-checkbox-blank-circle-fill me-1 align-middle"></i>
              {t("durationStatusUpcoming")}
            </span>
            <span className="text-success">
              <i className="ri-checkbox-blank-circle-fill me-1 align-middle"></i>
              {t("durationStatusActive")}
            </span>
          </div>

          {isLoading ? (
            <TableSpinner />
          ) : stages.length === 0 ? (
            <Card>
              <CardBody className="text-center text-muted py-5">
                {t("noStagesYetLead")}{" "}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline fw-medium"
                  onClick={() => {
                    setEditingItemId(null);
                    setShowAddEditModal(true);
                  }}
                >
                  {t("addStage")}
                </button>{" "}
                {t("noStagesYetTail")}
              </CardBody>
            </Card>
          ) : (
            <Row>
              {stages.map((stage) => (
                <Col key={stage.id} xl={6} className="mb-3">
                  <Card className="h-100 border">
                    <CardHeader className="d-flex justify-content-between align-items-start bg-transparent border-0">
                      <div>
                        <h5 className="mb-1">
                          {stage.sequenceNumber != null ? (
                            <span className="badge bg-soft-primary text-primary me-2">
                              #{stage.sequenceNumber}
                            </span>
                          ) : null}
                          {stage.name[langKey]}
                        </h5>
                        <small className="text-muted">{stage.code}</small>
                      </div>
                      <div className="hstack gap-1">
                        <ActionButton
                          onAction={() => {
                            setEditingItemId(stage.id);
                            setShowAddEditModal(true);
                          }}
                          actionType={ActionTypeEnum.EDIT}
                        />
                        <ActionButton
                          onAction={() => {
                            setDeletingItemId(stage.id);
                            setShowDeleteModal(true);
                          }}
                          actionType={ActionTypeEnum.DELETE}
                        />
                        <Button
                          color="info"
                          outline
                          size="sm"
                          onClick={() => {
                            setAddingGradeStageId(stage.id);
                            setEditingGrade(null);
                            setShowAddGradeModal(true);
                          }}
                        >
                          <i className="ri-add-line me-1"></i>
                          {t("addGrade")}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody>
                      {stage.grades.length === 0 ? (
                        <p className="text-muted mb-0 small">
                          {t("noGradesAssigned")}
                        </p>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {stage.grades.map((g) => {
                            const durationStatus = getDurationStatus(g);
                            return (
                              <div
                                key={g.id}
                                className="d-flex align-items-center justify-content-between gap-2 border rounded px-2 py-2"
                              >
                                <div className="min-w-0">
                                  <div className="fw-medium">
                                    {g.sequenceNumber != null ? (
                                      <span className="text-muted me-1">
                                        #{g.sequenceNumber}
                                      </span>
                                    ) : null}
                                    {g.name[langKey]}
                                  </div>
                                  <small
                                    className={classnames({
                                      "text-danger":
                                        durationStatus === "danger",
                                      "text-warning":
                                        durationStatus === "warning",
                                      "text-success":
                                        durationStatus === "success",
                                    })}
                                  >
                                    {formatDurationSummary(g)}
                                  </small>
                                </div>
                                <div className="hstack gap-1 flex-shrink-0">
                                  <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    onClick={() => {
                                      history.push(
                                        `/subjects${buildSubjectsSearch({
                                          educationSystemId:
                                            selectedEducationSystemId,
                                          curriculumId: selectedCurriculumId,
                                          courseId: stage.courseId,
                                          stageId: stage.id,
                                          gradeId: g.id,
                                        })}`,
                                      );
                                    }}
                                  >
                                    <i className="ri-book-2-line me-1"></i>
                                    {t("subjects")}
                                  </Button>
                                  <Button
                                    color="info"
                                    outline
                                    size="sm"
                                    onClick={() => {
                                      setAddingGradeStageId(stage.id);
                                      setEditingGrade(g);
                                      setShowAddGradeModal(true);
                                    }}
                                  >
                                    <i className="ri-pencil-line me-1"></i>
                                    {t("edit")}
                                  </Button>
                                  <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => {
                                      setDurationTarget({
                                        courseId: stage.courseId,
                                        gradeId: g.id,
                                      });
                                      setShowDurationModal(true);
                                    }}
                                  >
                                    {t("editDuration")}
                                  </Button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-soft-danger"
                                    onClick={() => {
                                      setDeletingGrade({
                                        stageId: stage.id,
                                        gradeId: g.id,
                                      });
                                      setShowDeleteGradeModal(true);
                                    }}
                                    aria-label={t("delete")}
                                  >
                                    <i className="ri-close-line"></i>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              ))}
              <Col xl={6} className="mb-3">
                <Card
                  className="h-100 border border-dashed d-flex align-items-center justify-content-center"
                  style={{ minHeight: 180, cursor: "pointer" }}
                  onClick={() => {
                    setEditingItemId(null);
                    setShowAddEditModal(true);
                  }}
                >
                  <CardBody className="text-center text-muted">
                    <i className="ri-add-circle-line display-6 d-block mb-2"></i>
                    {t("addStage")}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          <DeleteModal
            loading={deleting}
            show={showDeleteModal}
            onDeleteClick={onDeleteConfirm}
            onCloseClick={() => {
              setShowDeleteModal(false);
              setDeletingItemId(null);
            }}
            error={
              deleteError
                ? (deleteError as any)?.data?.message || "An error occurred"
                : undefined
            }
          />
          <DeleteModal
            loading={deletingGradeLoading}
            show={showDeleteGradeModal}
            onDeleteClick={onDeleteGradeConfirm}
            onCloseClick={() => {
              setShowDeleteGradeModal(false);
              setDeletingGrade(null);
            }}
            error={
              deleteGradeError
                ? (deleteGradeError as any)?.data?.message ||
                  "An error occurred"
                : undefined
            }
          />
          <AddEditEducationalStageModal
            id={editingItemId}
            defaultEducationSystemId={selectedEducationSystemId}
            defaultCurriculumId={selectedCurriculumId}
            defaultCourseId={selectedCourseId}
            show={showAddEditModal}
            onCloseClick={() => {
              setShowAddEditModal(false);
              setEditingItemId(null);
            }}
          />
          <AddGradeModal
            stageId={addingGradeStageId}
            grade={editingGrade}
            show={showAddGradeModal}
            onCloseClick={() => {
              setShowAddGradeModal(false);
              setAddingGradeStageId(null);
              setEditingGrade(null);
            }}
          />
          {durationTarget && (
            <CourseDurationModal
              courseId={durationTarget.courseId}
              gradeId={durationTarget.gradeId}
              show={showDurationModal}
              onCloseClick={() => {
                setShowDurationModal(false);
                setDurationTarget(null);
              }}
            />
          )}
          <ConfirmationModal
            confirmationButtonInProgressTitle={t("saving")}
            confirmationButtonTitle={t("rolloverNow")}
            description={t("rolloverDescription", {
              rollingToCourseName: getCourseName(selectedCourseId),
            })}
            icon={<></>}
            title={t("areYouSure")}
            loading={rolloverLoading}
            show={showRolloverModal}
            onConfirmClick={onRolloverConfirm}
            onCloseClick={() => setShowRolloverModal(false)}
            error={rolloverError}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export const EducationalStagesPage = withRouter(EducationalStagesPageComponent);
